/**
 * @krisspy-file
 * @type page
 * @name "InventoryItemForm"
 * @title "Inventory Item Form"
 * @description "Create or edit inventory item details"
 * @routes ["/inventory/create", "/inventory/:id/edit"]
 * @flowName "inventory-management"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import NumberInput from '../components/NumberInput';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Info,
  Shield,
  MapPin,
  DollarSign,
  Wrench,
  BarChart3,
  Calendar,
  Plus,
  Trash2,
  RotateCcw,
  Clock,
  Check,
  X,
  ExternalLink,
  User,
  ChevronDown,
  ChevronUp,
  Copy
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import {
  getInventoryItemById,
  createInventoryItems,
  getInventoryItemIdentifiers,
  updateInventoryItem,
  deleteInventoryItem,
  upsertInventoryItemTranslation,
  getInventoryItemTranslations,
  deleteInventoryItemTranslation
} from '../services/inventory.service';
import { getBookingsForInventoryItem } from '../services/bookings.service';
import {
  getBlockingTimesByInventoryItemId,
  createBlockingTime,
  updateBlockingTime,
  deleteBlockingTime,
  type InventoryBlockingTime
} from '../services/inventoryBlockingTimes.service';
import {
  getIssuesByInventoryItem,
  type IssueWithRelations,
} from '../services/issues.service';
import IssueDetail from '../components/IssueDetail';
import ReportIssueModal from '../components/ReportIssueModal';
import { getProducts } from '../services/products.service';
import { getLocations } from '../services/locations.service';
import { getMerchantById } from '../services/merchants.service';
import { supabase } from '../lib/supabase';
import { utcIsoToDatetimeLocalValue, datetimeLocalValueToUtcIso } from '../lib/timezone';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useAuth } from '../contexts/AuthContext';
import { useBarcodeScanning } from '../contexts/ScanContext';
import { parseBarcodeDuplicate } from '../lib/barcode';
import {
  allocateNrs,
  findItemNumberConflicts,
  generateItemNumbers,
  nextFreeItemNumber,
} from '../lib/inventoryNumbering';
import type { Database } from '../lib/database.types';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import BlockingPeriodConflictsModal from '../components/BlockingPeriodConflictsModal';

type InventoryItemInsert = Database['public']['Tables']['inventory_items']['Insert'];
type InventoryItemUpdate = Database['public']['Tables']['inventory_items']['Update'];

/** Upper bound for "create several at once"; above this the CSV import is the right tool. */
const MAX_COPIES = 200;
/** Tags shown in the multi-copy preview before it is abbreviated. */
const PREVIEW_TAGS = 6;

type TabType = 'basic' | 'security' | 'alternatives' | 'pricing' | 'maintenance' | 'bookings' | 'statistics';

interface ProductType {
  id: string;
  product_name: string;
  product_type: string;
  location_id: string | null;
  location_name: string | null;
}

export default function InventoryItemForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { currentLanguage, defaultLanguage, availableLanguages } = useLanguage();
  const { formatDate } = useDateFormatter();
  const { enabled: scanningEnabled } = useBarcodeScanning();
  const isEditMode = !!id;
  // /inventory/create?from=<id> opens the create form prefilled from that item.
  const duplicateFromId = !isEditMode ? searchParams.get('from') : null;
  const isDuplicateMode = !!duplicateFromId;

  const [loading, setLoading] = useState(isEditMode || isDuplicateMode);
  /** Tag of the item being duplicated, for the page subtitle. */
  const [sourceItemNumber, setSourceItemNumber] = useState<string | null>(null);
  /** How many items the create form saves at once. */
  const [quantity, setQuantity] = useState(1);
  /** Tags of items already under the chosen product type, for the multi-copy preview. */
  const [existingItemNumbers, setExistingItemNumbers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [blockingTimes, setBlockingTimes] = useState<InventoryBlockingTime[]>([]);
  const [issues, setIssues] = useState<IssueWithRelations[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [showBlockingConflictsModal, setShowBlockingConflictsModal] = useState(false);
  const [pendingSaveAfterConflicts, setPendingSaveAfterConflicts] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [allProductTypes, setAllProductTypes] = useState<ProductType[]>([]);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [merchantTimezone, setMerchantTimezone] = useState<string>('UTC');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [inventoryType, setInventoryType] = useState<string | null>(null);
  const [bookings, setBookings] = useState<any[] | null>(null);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [showAllPast, setShowAllPast] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const PAST_BOOKINGS_LIMIT = 15;

  // Translation state
  const [activeLanguageTab, setActiveLanguageTab] = useState(defaultLanguage);
  const [translations, setTranslations] = useState<Record<string, {
    inventory_specific_info: string;
  }>>({});
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);

  const [formData, setFormData] = useState({
    // Tab 1: Basic Information
    product_type_id: '',
    // null = assign the next free number of the product type on save.
    nr: null as number | null,
    item_number: '',
    barcode: '',
    size: '',
    active: true,
    inventory_specific_info: '',

    // Tab 2: Lock & Security
    has_lock: true,
    has_number: true,
    combination_code: '',
    backup_code: '',
    allocation_order: 0,

    // Tab 3: Alternative Products
    alt_product_type_id: '',
    additional_product_type_ids: [] as string[],

    // Tab 4: Availability
    pre_booking_hours: null as number | null,
    cutoff_days_prior: null as number | null,
    cutoff_time: '',

    // Tab 5: Maintenance
    last_maintenance_date: '',
    next_maintenance_date: '',
    maintenance_notes: '',

    // Tab 6: Usage Statistics (read-only)
    usage_times: 0,
    usage_hours: 0,
    usage_revenue: 0
  });

  useEffect(() => {
    loadProductTypes();
    loadLocations();
    loadMerchantCurrency();
    if (isEditMode && id) {
      loadInventoryItem();
    } else if (duplicateFromId) {
      loadSourceItem(duplicateFromId);
    }
  }, [id, duplicateFromId, defaultLanguage]);

  // Tags in use under the selected product type drive the multi-copy preview.
  // Only fetched while creating; the edit form never generates tags.
  useEffect(() => {
    if (isEditMode || !formData.product_type_id) {
      setExistingItemNumbers([]);
      return;
    }
    let cancelled = false;
    getInventoryItemIdentifiers(formData.product_type_id).then(({ data }) => {
      if (!cancelled) setExistingItemNumbers(data?.itemNumbers ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [isEditMode, formData.product_type_id]);

  // Load issues when maintenance tab is selected and we're in edit mode
  useEffect(() => {
    if (activeTab === 'maintenance' && isEditMode && id) {
      loadIssues();
    }
  }, [activeTab, id]);

  // Lazy-load bookings for this item the first time the Bookings tab is opened
  useEffect(() => {
    if (activeTab === 'bookings' && isEditMode && id && bookings === null && !bookingsLoading) {
      loadBookings();
    }
  }, [activeTab, id]);

  async function loadBookings() {
    if (!id) return;
    setBookingsLoading(true);
    setBookingsError(null);
    const { data, error: fetchError } = await getBookingsForInventoryItem(id, {
      merchantId,
      locationIds,
      isPlatformAdmin,
    });
    if (fetchError) {
      setBookingsError(fetchError);
    } else {
      setBookings(data || []);
    }
    setBookingsLoading(false);
  }

  async function loadIssues() {
    if (!id) return;
    setIssuesLoading(true);
    const { data } = await getIssuesByInventoryItem(id);
    setIssues(data || []);
    setIssuesLoading(false);
  }

  // Filter products when location filter changes
  useEffect(() => {
    if (locationFilter === 'all') {
      setProductTypes(allProductTypes);
    } else {
      setProductTypes(
        allProductTypes.filter((pt: any) => pt.location_id === locationFilter)
      );
    }
  }, [locationFilter, allProductTypes]);

  async function loadProductTypes() {
    // Fetch all products without location filtering
    const { data, error: fetchError } = await getProducts(
      { merchantId, locationIds: null, isPlatformAdmin },
      {}
    );

    if (!fetchError && data) {
      const products = data.map((p: any) => ({
        id: p.id,
        product_name: p.product_name,
        product_type: p.product_type,
        location_id: p.location_id,
        location_name: (p as any).locations?.location_name || null,
      }));
      setAllProductTypes(products);
      setProductTypes(products);
    }
  }

  async function loadLocations() {
    const { data, error: fetchError } = await getLocations(
      { merchantId, locationIds: null, isPlatformAdmin },
      { active: true }
    );

    if (!fetchError && data) {
      setLocations(
        data.map(loc => ({
          id: loc.id,
          name: loc.location_name
        }))
      );
    }
  }

  async function loadMerchantCurrency() {
    if (!merchantId) return;
    const { data } = await getMerchantById(merchantId);
    if (data?.currency_symbol) {
      setCurrencySymbol(data.currency_symbol);
    }
    if (data?.timezone) {
      setMerchantTimezone(data.timezone);
    }
  }

  async function loadInventoryItem() {
    if (!id) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getInventoryItemById(id, {
      merchantId,
      locationIds,
      isPlatformAdmin
    }, defaultLanguage);

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      setInventoryType(((data as any).product_types?.inventory_type as string | null) ?? null);
      setFormData({
        // Tab 1
        product_type_id: data.product_type_id || '',
        nr: data.nr ?? null,
        item_number: data.item_number || '',
        barcode: (data as any).barcode || '',
        size: data.size || '',
        active: data.active !== null ? data.active : true,
        inventory_specific_info: data.inventory_specific_info || '',

        // Tab 2
        has_lock: data.has_lock !== null ? data.has_lock : true,
        has_number: data.has_number !== null ? data.has_number : true,
        combination_code: data.combination_code || '',
        backup_code: data.backup_code || '',
        allocation_order: data.allocation_order || 0,

        // Tab 3 — merge alt_product_type_id into additional_product_type_ids for unified UI
        alt_product_type_id: data.alt_product_type_id || '',
        additional_product_type_ids: [
          ...(data.alt_product_type_id ? [data.alt_product_type_id] : []),
          ...(data.additional_product_type_ids || []),
        ].filter((id, i, arr) => arr.indexOf(id) === i), // dedupe

        // Tab 4
        pre_booking_hours: data.pre_booking_hours,
        cutoff_days_prior: data.cutoff_days_prior,
        cutoff_time: data.cutoff_time || '',

        // Tab 5
        last_maintenance_date: data.last_maintenance_date || '',
        next_maintenance_date: data.next_maintenance_date || '',
        maintenance_notes: data.maintenance_notes || '',

        // Tab 6
        usage_times: data.usage_times || 0,
        usage_hours: data.usage_hours || 0,
        usage_revenue: data.usage_revenue || 0
      });

      // Load blocking times
      const { data: blockingData, error: blockingError } = await getBlockingTimesByInventoryItemId(id);
      if (!blockingError && blockingData) {
        setBlockingTimes(blockingData);
      }

      // Seed existing translations so previously-saved values are visible and
      // re-editable on reload (otherwise the translation tabs always show empty).
      const { data: translationRows } = await getInventoryItemTranslations(id);
      if (translationRows && translationRows.length > 0) {
        const seeded: Record<string, { inventory_specific_info: string }> = {};
        for (const row of translationRows) {
          seeded[row.language_code] = {
            inventory_specific_info: row.inventory_specific_info || '',
          };
        }
        setTranslations(seeded);
      }
    }

    setLoading(false);
  }

  /**
   * Prefill the create form from an existing item. Copies what describes the
   * kind of item (product type, size, flags, codes, allocation, availability,
   * alternative product types, email text and its translations) and leaves out
   * what identifies or records the physical unit: tag, nr, barcode, usage,
   * maintenance history and blocking periods. The tag is suggested as the next
   * free one in the source's numbering.
   */
  async function loadSourceItem(sourceId: string) {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getInventoryItemById(sourceId, {
      merchantId,
      locationIds,
      isPlatformAdmin
    }, defaultLanguage);

    if (fetchError || !data) {
      setError(fetchError || t('inventoryForm.duplicateSourceNotFound'));
      setLoading(false);
      return;
    }

    const { data: identifiers } = await getInventoryItemIdentifiers(data.product_type_id);
    const takenTags = identifiers?.itemNumbers ?? [];

    setSourceItemNumber(data.item_number || null);
    setInventoryType(((data as any).product_types?.inventory_type as string | null) ?? null);
    setFormData((prev) => ({
      ...prev,
      product_type_id: data.product_type_id || '',
      nr: null,
      item_number: data.item_number ? nextFreeItemNumber(data.item_number, takenTags) : '',
      barcode: '',
      size: data.size || '',
      active: data.active !== null ? data.active : true,
      inventory_specific_info: data.inventory_specific_info || '',
      has_lock: data.has_lock !== null ? data.has_lock : true,
      has_number: data.has_number !== null ? data.has_number : true,
      combination_code: data.combination_code || '',
      backup_code: data.backup_code || '',
      allocation_order: data.allocation_order || 0,
      alt_product_type_id: data.alt_product_type_id || '',
      additional_product_type_ids: [
        ...(data.alt_product_type_id ? [data.alt_product_type_id] : []),
        ...(data.additional_product_type_ids || []),
      ].filter((ptId, i, arr) => arr.indexOf(ptId) === i),
      pre_booking_hours: data.pre_booking_hours,
      cutoff_days_prior: data.cutoff_days_prior,
      cutoff_time: data.cutoff_time || '',
      // Not copied: a new unit has its own history.
      last_maintenance_date: '',
      next_maintenance_date: '',
      maintenance_notes: '',
      usage_times: 0,
      usage_hours: 0,
      usage_revenue: 0,
    }));

    const { data: translationRows } = await getInventoryItemTranslations(sourceId);
    if (translationRows && translationRows.length > 0) {
      const seeded: Record<string, { inventory_specific_info: string }> = {};
      for (const row of translationRows) {
        seeded[row.language_code] = {
          inventory_specific_info: row.inventory_specific_info || '',
        };
      }
      setTranslations(seeded);
    }

    setLoading(false);
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.product_type_id) {
      errors.product_type_id = t('inventoryForm.productTypeRequired');
    }

    if (!formData.item_number || !formData.item_number.trim()) {
      errors.item_number = t('inventoryForm.itemTagRequired');
    }

    if (!isEditMode && (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_COPIES)) {
      errors.quantity = t('inventoryForm.quantityRange', { max: MAX_COPIES });
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // In edit mode with blocking periods, check for booking conflicts first
    if (isEditMode && id && blockingTimes.length > 0 && !pendingSaveAfterConflicts) {
      // Quick check: do any blocking periods overlap with existing bookings?
      const hasConflicts = await checkBlockingPeriodConflicts();
      if (hasConflicts) {
        setShowBlockingConflictsModal(true);
        return;
      }
    }

    // Reset the flag
    setPendingSaveAfterConflicts(false);

    await performSave();
  }

  /** Check if any blocking period overlaps with open/active bookings on this item */
  async function checkBlockingPeriodConflicts(): Promise<boolean> {
    if (!id) return false;

    try {
      const { data: bookingItems } = await supabase
        .from('booking_items')
        .select(`
          id,
          bookings!inner(
            start_date,
            end_date,
            booking_state
          )
        `)
        .eq('inventory_item_id', id)
        .in('bookings.booking_state', ['open', 'active']);

      if (!bookingItems || bookingItems.length === 0) return false;

      // Check if any booking overlaps with any blocking period
      for (const bi of bookingItems as any[]) {
        const booking = bi.bookings;
        if (!booking) continue;

        const bStart = new Date(booking.start_date);
        const bEnd = new Date(booking.end_date);

        for (const bt of blockingTimes) {
          const btStart = new Date(bt.blocked_from);
          const btEnd = new Date(bt.blocked_until);

          if (btStart < bEnd && btEnd > bStart) {
            return true; // Found a conflict
          }
        }
      }

      return false;
    } catch (err) {
      console.error('Error checking blocking period conflicts:', err);
      return false; // Proceed with save on error
    }
  }

  async function performSave() {
    setSaving(true);
    setError(null);

    try {
    const dataToSave: any = {
      product_type_id: formData.product_type_id,
      item_number: formData.item_number.trim(),
      size: formData.size || null,
      active: formData.active,
      inventory_specific_info: formData.inventory_specific_info || null,
      has_lock: formData.has_lock,
      has_number: formData.has_number,
      combination_code: formData.combination_code || null,
      backup_code: formData.backup_code || null,
      allocation_order: formData.allocation_order || 0,
      alt_product_type_id: formData.additional_product_type_ids.length > 0 ? formData.additional_product_type_ids[0] : null,
      additional_product_type_ids: formData.additional_product_type_ids.length > 1 ? formData.additional_product_type_ids.slice(1) : [],
      pre_booking_hours: formData.pre_booking_hours,
      cutoff_days_prior: formData.cutoff_days_prior,
      cutoff_time: formData.cutoff_time || null,
      last_maintenance_date: formData.last_maintenance_date || null,
      next_maintenance_date: formData.next_maintenance_date || null,
      maintenance_notes: formData.maintenance_notes || null
    };
    // Barcode is only written while scanning is on, so saving an item for a
    // merchant without scanning never touches the column.
    if (scanningEnabled) {
      dataToSave.barcode = formData.barcode.trim() || null;
    }

    if (isEditMode && id) {
      const updateData: InventoryItemUpdate = {
        ...dataToSave,
        // `nr` is NOT NULL: an emptied field keeps the stored number.
        ...(formData.nr != null ? { nr: formData.nr } : {}),
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await updateInventoryItem(id, updateData);

      if (updateError) {
        const duplicateOwner = parseBarcodeDuplicate(updateError);
        showToast(
          duplicateOwner
            ? t('scan.barcodeDuplicate', { owner: duplicateOwner })
            : t('inventoryForm.failedToUpdate') + ': ' + updateError,
          'error'
        );
        setSaving(false);
        return;
      }

      // Save blocking times
      for (const blocking of blockingTimes) {
        if (blocking.id.startsWith('temp-')) {
          const { error: createBlockingError } = await createBlockingTime({
            inventory_item_id: id,
            blocked_from: blocking.blocked_from,
            blocked_until: blocking.blocked_until,
            blocking_reason: blocking.blocking_reason,
            notes: blocking.notes || null
          });
          if (createBlockingError) {
            console.error('Failed to create blocking time:', createBlockingError);
          }
        } else {
          const { error: updateBlockingError } = await updateBlockingTime(blocking.id, {
            blocked_from: blocking.blocked_from,
            blocked_until: blocking.blocked_until,
            blocking_reason: blocking.blocking_reason,
            notes: blocking.notes || null
          });
          if (updateBlockingError) {
            console.error('Failed to update blocking time:', updateBlockingError);
          }
        }
      }

      // Save translations for all languages (except default). A cleared value
      // deletes the row so the deletion actually persists instead of leaving
      // the previous translation in place.
      for (const [langCode, translationData] of Object.entries(translations)) {
        if (langCode === defaultLanguage) continue;
        if (translationData.inventory_specific_info?.trim()) {
          const { error: translationError } = await upsertInventoryItemTranslation(
            id,
            langCode,
            {
              inventory_specific_info: translationData.inventory_specific_info
            }
          );
          if (translationError) {
            console.error(`Failed to save ${langCode} translation:`, translationError);
          }
        } else {
          const { error: translationError } = await deleteInventoryItemTranslation(id, langCode);
          if (translationError) {
            console.error(`Failed to delete ${langCode} translation:`, translationError);
          }
        }
      }

      showToast(t('inventoryForm.updatedSuccessfully'), 'success');
      navigate('/inventory');
    } else {
      // Tags and nrs are unique per product type. Read what is in use right
      // before inserting so the generated identifiers skip existing items and
      // a typed tag that clashes is reported here instead of as a raw 23505.
      const { data: identifiers, error: identifiersError } = await getInventoryItemIdentifiers(
        formData.product_type_id
      );
      if (identifiersError || !identifiers) {
        showToast(t('inventoryForm.failedToCreate') + ': ' + (identifiersError || ''), 'error');
        setSaving(false);
        return;
      }

      const count = Math.max(1, quantity);
      const tags = generateItemNumbers(formData.item_number, count, identifiers.itemNumbers);
      const conflicts = findItemNumberConflicts(tags, identifiers.itemNumbers);
      if (conflicts.length > 0) {
        const message = t('inventoryForm.itemTagTaken', { tags: conflicts.join(', ') });
        setValidationErrors((prev) => ({ ...prev, item_number: message }));
        setActiveTab('basic');
        showToast(message, 'error');
        setSaving(false);
        return;
      }
      const nrs = allocateNrs(formData.nr, count, identifiers.nrs);

      const rows: InventoryItemInsert[] = tags.map((tag, i) => ({
        ...dataToSave,
        item_number: tag,
        nr: nrs[i],
        // A barcode identifies one physical unit, so it is only written for a
        // single item; copies get theirs scanned in afterwards.
        ...(scanningEnabled && count > 1 ? { barcode: null } : {}),
      }));

      const { data: newItems, error: createError } = await createInventoryItems(rows);

      if (createError || !newItems) {
        const duplicateOwner = parseBarcodeDuplicate(createError);
        showToast(
          duplicateOwner
            ? t('scan.barcodeDuplicate', { owner: duplicateOwner })
            : t('inventoryForm.failedToCreate') + ': ' + createError,
          'error'
        );
        setSaving(false);
        return;
      }

      for (const newItem of newItems) {
        // Save blocking times for new item
        for (const blocking of blockingTimes) {
          if (blocking.id.startsWith('temp-')) {
            const { error: createBlockingError } = await createBlockingTime({
              inventory_item_id: newItem.id,
              blocked_from: blocking.blocked_from,
              blocked_until: blocking.blocked_until,
              blocking_reason: blocking.blocking_reason,
              notes: blocking.notes || null
            });
            if (createBlockingError) {
              console.error('Failed to create blocking time:', createBlockingError);
            }
          }
        }

        // Save translations for all languages (except default)
        for (const [langCode, translationData] of Object.entries(translations)) {
          if (langCode !== defaultLanguage && translationData.inventory_specific_info) {
            const { error: translationError } = await upsertInventoryItemTranslation(
              newItem.id,
              langCode,
              {
                inventory_specific_info: translationData.inventory_specific_info || null
              }
            );
            if (translationError) {
              console.error(`Failed to save ${langCode} translation:`, translationError);
            }
          }
        }
      }

      showToast(
        newItems.length > 1
          ? t('inventoryForm.createdSuccessfullyCount', { count: newItems.length })
          : t('inventoryForm.createdSuccessfully'),
        'success'
      );
      navigate('/inventory', { state: { reload: Date.now() } });
    }
    } catch (err) {
      console.error('Error saving inventory item:', err);
      showToast(t('inventoryForm.errorSaving'), 'error');
      setSaving(false);
    }
  }

  /** Called after user resolves blocking period conflicts in the modal */
  function handleBlockingConflictsResolved() {
    setPendingSaveAfterConflicts(true);
  }

  /** Called when user chooses to proceed despite unresolved conflicts */
  function handleProceedWithConflicts() {
    setPendingSaveAfterConflicts(true);
  }

  // Trigger save after conflict resolution
  useEffect(() => {
    if (pendingSaveAfterConflicts) {
      performSave();
    }
  }, [pendingSaveAfterConflicts]);

  async function handleDelete() {
    if (!id) return;

    setDeleting(true);
    setError(null);

    const { error: deleteError } = await deleteInventoryItem(id);

    if (deleteError) {
      setError(deleteError);
      setDeleting(false);
      // Keep modal open to show the error and deactivate option
      return;
    }

    // Navigate with state to trigger reload
    navigate('/inventory', { state: { reload: Date.now() } });
  }

  async function handleDeactivate() {
    if (!id) return;

    setDeleting(true);
    setError(null);

    const { error: updateError } = await updateInventoryItem(id, {
      active: false,
      updated_at: new Date().toISOString()
    });

    if (updateError) {
      setError(updateError);
      setDeleting(false);
      setShowDeleteConfirm(false);
      return;
    }

    // Navigate with state to trigger reload
    navigate('/inventory', { state: { reload: Date.now() } });
  }

  // Helper functions for blocking times
  const addBlockingTime = () => {
    const now = new Date();
    const newBlocking: Partial<InventoryBlockingTime> = {
      id: `temp-${Date.now()}`,
      inventory_item_id: id || '',
      blocked_from: now.toISOString(),
      blocked_until: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      blocking_reason: 'maintenance',
      notes: '',
      created_by_user_id: null,
      created_at: now.toISOString(),
      updated_at: now.toISOString()
    };
    setBlockingTimes(prev => [...prev, newBlocking as InventoryBlockingTime]);
  };

  const removeBlockingTime = async (blockingId: string) => {
    if (blockingId.startsWith('temp-')) {
      setBlockingTimes(prev => prev.filter(b => b.id !== blockingId));
      return;
    }

    const { error } = await deleteBlockingTime(blockingId);
    if (error) {
      showToast(t('inventoryForm.failedToDeleteBlocking') + ': ' + error, 'error');
      return;
    }
    setBlockingTimes(prev => prev.filter(b => b.id !== blockingId));
  };

  const updateBlockingTimeField = (index: number, field: keyof InventoryBlockingTime, value: any) => {
    setBlockingTimes(prev => prev.map((blocking, i) =>
      i === index ? { ...blocking, [field]: value } : blocking
    ));
  };

  const showBookingsTab = isEditMode && inventoryType === 'individual';

  /** "BIKE-001, BIKE-002, …, BIKE-010" for the multi-copy hint. */
  const previewTags = (first: string, count: number, taken: string[]) => {
    const tags = generateItemNumbers(first, count, taken);
    if (tags.length <= PREVIEW_TAGS) return tags.join(', ');
    return `${tags.slice(0, PREVIEW_TAGS - 1).join(', ')}, …, ${tags[tags.length - 1]}`;
  };

  const getBookingStateBadgeClass = (state: string | null | undefined) => {
    switch (state) {
      case 'active':
      case 'returned':
        return 'badge-success';
      case 'cancelled':
        return 'badge-danger';
      case 'open':
      default:
        return 'badge-info';
    }
  };

  const tabs = [
    { id: 'basic' as TabType, label: t('inventoryForm.tabBasic'), icon: Info },
    { id: 'security' as TabType, label: t('inventoryForm.tabSecurity'), icon: Shield },
    { id: 'alternatives' as TabType, label: t('inventoryForm.tabCrossLocation'), icon: MapPin },
    { id: 'pricing' as TabType, label: t('inventoryForm.tabPricing'), icon: Clock },
    { id: 'maintenance' as TabType, label: t('inventoryForm.tabMaintenance'), icon: Wrench },
    ...(showBookingsTab
      ? [{ id: 'bookings' as TabType, label: t('inventoryForm.tabBookings'), icon: Calendar }]
      : []),
    { id: 'statistics' as TabType, label: t('inventoryForm.tabStatistics'), icon: BarChart3 }
  ];

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/inventory')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.loading')}</h1>
        </div>
        <LoadingPlaceholder />
      </div>
    );
  }

  if (error && isEditMode) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/inventory')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.error')}</h1>
        </div>
        <div className="card">
          <div className="flex items-center gap-md text-danger">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/inventory')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-xs">
              {isEditMode
                ? t('inventoryForm.editItem')
                : isDuplicateMode
                  ? t('inventoryForm.duplicateItem')
                  : t('inventoryForm.createItem')}
            </h1>
            <p className="text-sm sm:text-base text-secondary">
              {isEditMode
                ? t('inventoryForm.updateDetails')
                : isDuplicateMode
                  ? t('inventoryForm.duplicateDetails', { source: sourceItemNumber || '' })
                  : t('inventoryForm.addNewItem')}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-sm sm:gap-md flex-shrink-0">
          {isEditMode && (
            <button
              type="button"
              onClick={() => navigate(`/inventory/create?from=${id}`)}
              className="btn btn-ghost text-sm"
              disabled={saving || deleting}
              data-testid="inventory-item-duplicate"
            >
              <Copy className="w-4 h-4" />
              <span className="hidden sm:inline">{t('inventoryForm.duplicate')}</span>
            </button>
          )}
          {isEditMode && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="btn btn-ghost text-danger hover:bg-danger/10 text-sm"
              disabled={saving || deleting}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.delete')}</span>
            </button>
          )}
          <button type="button" onClick={() => navigate('/inventory')} className="btn btn-ghost text-sm" disabled={saving || deleting}>
            <X className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.cancel')}</span>
          </button>
          <button type="button" onClick={handleSubmit} className="btn btn-primary text-sm" disabled={saving || deleting}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">{t('common.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">{isEditMode ? t('inventoryForm.updateItem') : t('inventoryForm.createItem')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="card bg-danger/10 border border-danger/20">
          <div className="flex items-center gap-md text-danger">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Tabs Navigation + Content in single card */}
        <div className="card p-0 overflow-hidden">
          <div className="flex border-b border-primary overflow-x-auto scrollbar-thin">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-sm px-sm sm:px-md lg:px-lg py-sm sm:py-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue border-b-2 border-blue bg-blue/5'
                      : 'text-secondary hover:text-primary hover:bg-secondary'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-sm sm:p-md lg:p-lg">
          {/* Tab 1: Basic Information */}
          {activeTab === 'basic' && (
            <div className="space-y-xl">

              {/* Location Filter */}
              {locations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.filterByLocation')}
                  </label>
                  <select
                    value={locationFilter}
                    onChange={(e) => {
                      setLocationFilter(e.target.value);
                      // Reset product selection if current product is not in new location
                      if (e.target.value !== 'all' && formData.product_type_id) {
                        const currentProduct = allProductTypes.find(pt => pt.id === formData.product_type_id);
                        if (currentProduct && currentProduct.location_id !== e.target.value) {
                          setFormData({ ...formData, product_type_id: '' });
                        }
                      }
                    }}
                    className="input w-full md:w-1/2"
                  >
                    <option value="all">{t('products.allLocations')}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-secondary mt-xs">
                    {t('inventoryForm.filterByLocationDesc')}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.productType')} <span className="text-danger">*</span>
                  </label>
                  <select
                    value={formData.product_type_id}
                    onChange={(e) => setFormData({ ...formData, product_type_id: e.target.value })}
                    className={`input w-full ${validationErrors.product_type_id ? 'border-danger' : ''}`}
                  >
                    <option value="">{t('products.selectProductType')}</option>
                    {productTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>
                        {pt.product_name} ({pt.product_type})
                      </option>
                    ))}
                  </select>
                  {validationErrors.product_type_id && (
                    <p className="text-danger text-xs mt-xs">{validationErrors.product_type_id}</p>
                  )}
                  {locationFilter !== 'all' && productTypes.length === 0 && (
                    <p className="text-warning text-xs mt-xs">
                      {t('inventoryForm.noProductsForLocation')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.itemTag')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.item_number}
                    onChange={(e) => setFormData({ ...formData, item_number: e.target.value })}
                    className={`input w-full ${validationErrors.item_number ? 'border-danger' : ''}`}
                    placeholder={t('inventoryForm.itemTagPlaceholder')}
                  />
                  {validationErrors.item_number && (
                    <p className="text-danger text-xs mt-xs">{validationErrors.item_number}</p>
                  )}
                  <p className="text-xs text-secondary mt-xs">
                    {t('inventoryForm.itemTagDesc')}
                  </p>
                </div>

                {scanningEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('scan.barcodeLabel')}
                    </label>
                    <input
                      type="text"
                      value={quantity > 1 ? '' : formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      className="input w-full font-mono"
                      placeholder={t('scan.barcodePlaceholder')}
                      disabled={quantity > 1}
                      data-barcode-input
                      data-testid="inventory-item-barcode-input"
                    />
                    <p className="text-xs text-secondary mt-xs">
                      {quantity > 1 ? t('inventoryForm.barcodePerItemHint') : t('scan.barcodeItemHint')}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.number')}
                  </label>
                  <NumberInput
                    nullable
                    value={formData.nr}
                    onChange={(v) => setFormData({ ...formData, nr: v != null && v > 0 ? v : null })}
                    mode="int"
                    min={1}
                    className="input w-full"
                    placeholder={t('inventoryForm.numberAutoPlaceholder')}
                  />
                  <p className="text-xs text-secondary mt-xs">
                    {isEditMode
                      ? t('inventoryForm.numberDescEdit')
                      : quantity > 1
                        ? t('inventoryForm.numberDescCopies')
                        : t('inventoryForm.numberDesc')}
                  </p>
                </div>

                {!isEditMode && (
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('inventoryForm.quantity')}
                    </label>
                    <NumberInput
                      value={quantity}
                      onChange={(v) => setQuantity(v)}
                      mode="int"
                      min={1}
                      max={MAX_COPIES}
                      className={`input w-full ${validationErrors.quantity ? 'border-danger' : ''}`}
                      data-testid="inventory-item-quantity-input"
                    />
                    {validationErrors.quantity && (
                      <p className="text-danger text-xs mt-xs">{validationErrors.quantity}</p>
                    )}
                    <p className="text-xs text-secondary mt-xs">
                      {quantity > 1 && formData.item_number.trim()
                        ? t('inventoryForm.quantityPreview', {
                            tags: previewTags(formData.item_number, quantity, existingItemNumbers),
                          })
                        : t('inventoryForm.quantityDesc', { max: MAX_COPIES })}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.sizeOptional')}
                  </label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    className="input w-full"
                    placeholder="e.g., Medium, Large, XL"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-sm">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('common.active')}</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('inventoryForm.inventorySpecificText')}
                </label>
                <textarea
                  value={formData.inventory_specific_info}
                  onChange={(e) => setFormData({ ...formData, inventory_specific_info: e.target.value })}
                  className="input w-full"
                  rows={4}
                  placeholder={t('inventoryForm.inventorySpecificTextPlaceholder')}
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('inventoryForm.inventorySpecificTextDesc')}
                </p>
              </div>

              {/* Translations Section */}
              {availableLanguages.length > 1 && (
                <div className="border-t border-primary pt-lg">
                  <h3 className="text-base font-semibold text-primary mb-md">{t('products.translations')}</h3>

                  {/* Language tabs */}
                  <div className="flex gap-sm mb-lg overflow-x-auto">
                    {availableLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setActiveLanguageTab(lang.code)}
                        className={`px-md py-sm rounded-md flex items-center gap-xs whitespace-nowrap transition-colors ${
                          activeLanguageTab === lang.code
                            ? 'bg-blue text-white'
                            : 'bg-secondary text-secondary hover:bg-primary/10'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="space-y-md">
                    {activeLanguageTab === defaultLanguage ? (
                      <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                          <strong>{t('inventoryForm.inventorySpecificText')}:</strong> {formData.inventory_specific_info || <em>({t('common.none')})</em>}
                        </p>
                        <p className="text-xs text-secondary mt-md">
                          {t('inventoryForm.mainLanguageEdit')}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-primary mb-sm">
                          {t('inventoryForm.inventorySpecificText')} ({activeLanguageTab.toUpperCase()})
                        </label>
                        <textarea
                          value={translations[activeLanguageTab]?.inventory_specific_info || ''}
                          onChange={(e) => setTranslations(prev => ({
                            ...prev,
                            [activeLanguageTab]: {
                              inventory_specific_info: e.target.value
                            }
                          }))}
                          className="input w-full"
                          rows={4}
                          placeholder={t('settings.addTranslationVersion', { language: availableLanguages.find(l => l.code === activeLanguageTab)?.label })}
                        />
                        <p className="text-xs text-secondary mt-xs">
                          {t('inventoryForm.inventorySpecificTextDesc')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Lock & Security */}
          {activeTab === 'security' && (
            <div className="space-y-xl">

              <div className="space-y-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('inventoryForm.combinationCode')}
                    </label>
                    <input
                      type="text"
                      value={formData.combination_code}
                      onChange={(e) => setFormData({ ...formData, combination_code: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 1234"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-sm">
                      {t('inventoryForm.backupCode')}
                    </label>
                    <input
                      type="text"
                      value={formData.backup_code}
                      onChange={(e) => setFormData({ ...formData, backup_code: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., 5678"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.allocationOrder')}
                  </label>
                  <NumberInput
                    value={formData.allocation_order}
                    onChange={(v) => setFormData({ ...formData, allocation_order: v || 0 })}
                    mode="int"
                    min={0}
                    className="input w-full"
                    placeholder="0"
                  />
                  <p className="text-xs text-secondary mt-xs">
                    {t('inventoryForm.allocationOrderDesc')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Cross-Location Availability */}
          {activeTab === 'alternatives' && (() => {
            // Derive current item's primary product type for context display
            const primaryPt = allProductTypes.find(pt => pt.id === formData.product_type_id);

            // Group ALL product types by location (excluding the primary one)
            const otherPts = allProductTypes.filter(pt => pt.id !== formData.product_type_id);
            const groupedByLocation: Record<string, { locationName: string; items: ProductType[] }> = {};
            for (const pt of otherPts) {
              const key = pt.location_id || '_none';
              if (!groupedByLocation[key]) {
                groupedByLocation[key] = {
                  locationName: pt.location_name || t('inventoryForm.noLocation'),
                  items: [],
                };
              }
              groupedByLocation[key].items.push(pt);
            }

            const toggleProductType = (ptId: string) => {
              setFormData(prev => {
                const ids = prev.additional_product_type_ids;
                return {
                  ...prev,
                  additional_product_type_ids: ids.includes(ptId)
                    ? ids.filter(id => id !== ptId)
                    : [...ids, ptId],
                };
              });
            };

            return (
              <div className="space-y-xl">
                <p className="text-sm text-secondary">
                  {t('inventoryForm.crossLocationDesc')}
                </p>

                {/* Primary product type context */}
                {primaryPt && (
                  <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                    <p className="text-sm text-primary">
                      <span className="font-medium">{t('inventoryForm.primaryProductType')}:</span>{' '}
                      <strong>{primaryPt.product_name}</strong> ({primaryPt.product_type})
                      {primaryPt.location_name && (
                        <> at <strong>{primaryPt.location_name}</strong></>
                      )}
                    </p>
                    <p className="text-xs text-secondary mt-xs">
                      {t('inventoryForm.selectAdditionalProductTypes')}
                    </p>
                  </div>
                )}

                {/* Grouped checkbox list */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-md">
                    {t('inventoryForm.additionalProductTypes')}
                  </label>

                  {Object.keys(groupedByLocation).length === 0 ? (
                    <p className="text-sm text-secondary">{t('inventoryForm.noOtherProductTypes')}</p>
                  ) : (
                    <div className="space-y-lg">
                      {Object.entries(groupedByLocation).map(([locId, group]) => (
                        <div key={locId}>
                          <div className="flex items-center gap-sm mb-sm">
                            <MapPin className="w-4 h-4 text-secondary" />
                            <span className="text-sm font-semibold text-secondary uppercase tracking-wide">
                              {group.locationName}
                            </span>
                          </div>
                          <div className="space-y-xs ml-md">
                            {group.items.map(pt => {
                              const isSelected = formData.additional_product_type_ids.includes(pt.id);
                              return (
                                <div
                                  key={pt.id}
                                  role="checkbox"
                                  aria-checked={isSelected}
                                  tabIndex={0}
                                  onClick={() => toggleProductType(pt.id)}
                                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleProductType(pt.id); } }}
                                  className={`flex items-center gap-md p-sm rounded-md cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-blue/5 border border-blue/20'
                                      : 'hover:bg-hover border border-transparent'
                                  }`}
                                >
                                  <div className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 transition-colors ${
                                    isSelected
                                      ? 'bg-blue border-blue text-white'
                                      : 'border-primary'
                                  }`}>
                                    {isSelected && <Check className="w-3 h-3" />}
                                  </div>
                                  <div>
                                    <span className="text-sm text-primary font-medium">
                                      {pt.product_name}
                                    </span>
                                    <span className="text-xs text-secondary ml-sm">
                                      ({pt.product_type})
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {formData.additional_product_type_ids.length > 0 && (
                    <p className="text-xs text-secondary mt-md">
                      {t('inventoryForm.additionalProductTypesSelected', { count: formData.additional_product_type_ids.length })}
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Tab 4: Availability */}
          {activeTab === 'pricing' && (
            <div className="space-y-xl">
              <p className="text-sm text-secondary">
                {t('inventoryForm.pricingOverrideDesc')}
              </p>

              <div className="space-y-lg">
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('products.preBookingHoursLabel')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.pre_booking_hours || ''}
                    onChange={(e) => setFormData({ ...formData, pre_booking_hours: e.target.value ? parseFloat(e.target.value) : null })}
                    className="input w-32"
                    placeholder="e.g., 24"
                  />
                  <p className="text-xs text-secondary mt-xs">{t('products.preBookingHoursHelperText')}</p>
                </div>

                <div className="border border-primary rounded-lg p-md">
                  <h4 className="text-sm font-semibold text-primary mb-md">{t('products.cutoffGroupLabel')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('products.cutoffDaysPriorLabel')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.cutoff_days_prior ?? ''}
                        onChange={(e) => setFormData({ ...formData, cutoff_days_prior: e.target.value === '' ? null : parseInt(e.target.value) })}
                        className="input w-32"
                        placeholder="e.g., 2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('products.cutoffTimeLabel')}
                      </label>
                      <input
                        type="time"
                        value={formData.cutoff_time}
                        onChange={(e) => setFormData({ ...formData, cutoff_time: e.target.value })}
                        className="input w-32"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-secondary mt-md">
                    {t('products.cutoffHelperText')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tab 5: Maintenance */}
          {activeTab === 'maintenance' && (
            <div className="space-y-xl">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.lastMaintenanceDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.last_maintenance_date}
                    onChange={(e) => setFormData({ ...formData, last_maintenance_date: e.target.value })}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-sm">
                    {t('inventoryForm.nextMaintenanceDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.next_maintenance_date}
                    onChange={(e) => setFormData({ ...formData, next_maintenance_date: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('inventoryForm.maintenanceNotes')}
                </label>
                <textarea
                  value={formData.maintenance_notes}
                  onChange={(e) => setFormData({ ...formData, maintenance_notes: e.target.value })}
                  className="input w-full"
                  rows={4}
                  placeholder={t('inventoryForm.maintenanceNotesPlaceholder')}
                />
              </div>

              {/* Blocking Periods */}
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('inventoryForm.blockingPeriods')}
                </label>
                <p className="text-xs text-secondary mb-sm">
                  {t('inventoryForm.blockingPeriodsDesc')}
                </p>
                <div className="space-y-sm border border-border rounded-lg p-md">
                  {blockingTimes.length === 0 ? (
                    <p className="text-xs text-secondary italic">{t('inventoryForm.noBlockingPeriods')}</p>
                  ) : (
                    blockingTimes.map((blocking, index) => (
                      <div key={blocking.id} className="flex items-start gap-md p-sm bg-secondary rounded-lg">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-md">
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('inventoryForm.blockedFrom')}
                            </label>
                            <input
                              type="datetime-local"
                              value={utcIsoToDatetimeLocalValue(blocking.blocked_from, merchantTimezone)}
                              onChange={(e) => updateBlockingTimeField(index, 'blocked_from', datetimeLocalValueToUtcIso(e.target.value, merchantTimezone))}
                              className="input text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('inventoryForm.blockedUntil')}
                            </label>
                            <input
                              type="datetime-local"
                              value={utcIsoToDatetimeLocalValue(blocking.blocked_until, merchantTimezone)}
                              onChange={(e) => updateBlockingTimeField(index, 'blocked_until', datetimeLocalValueToUtcIso(e.target.value, merchantTimezone))}
                              className="input text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('inventoryForm.reason')}
                            </label>
                            <select
                              value={blocking.blocking_reason}
                              onChange={(e) => updateBlockingTimeField(index, 'blocking_reason', e.target.value)}
                              className="input text-sm w-full"
                            >
                              <option value="maintenance">{t('inventoryForm.reasonMaintenance')}</option>
                              <option value="repair">{t('inventoryForm.reasonRepair')}</option>
                              <option value="reserved">{t('inventoryForm.reasonReserved')}</option>
                              <option value="seasonal">{t('inventoryForm.reasonSeasonal')}</option>
                              <option value="other">{t('inventoryForm.reasonOther')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-secondary mb-xs">
                              {t('inventoryForm.notes')}
                            </label>
                            <input
                              type="text"
                              value={blocking.notes || ''}
                              onChange={(e) => updateBlockingTimeField(index, 'notes', e.target.value)}
                              placeholder={t('inventoryForm.optionalNotes')}
                              className="input text-sm w-full"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeBlockingTime(blocking.id)}
                          className="btn-icon btn-sm text-danger mt-6"
                          title={t('inventoryForm.removeBlockingPeriod')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                  <button
                    type="button"
                    onClick={addBlockingTime}
                    className="btn btn-ghost w-full"
                  >
                    <Plus className="w-4 h-4" />
                    {t('inventoryForm.addBlockingPeriod')}
                  </button>
                </div>
              </div>

              {/* Issues section */}
              {isEditMode && id && (
                <div>
                  <div className="flex items-center justify-between mb-sm">
                    <label className="block text-sm font-medium text-primary">
                      {t('inventoryForm.issues')}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowReportIssueModal(true)}
                      className="btn btn-sm btn-ghost text-warning hover:bg-warning/10"
                    >
                      <AlertTriangle className="w-4 h-4" />
                      {t('inventoryForm.reportIssue')}
                    </button>
                  </div>
                  <p className="text-xs text-secondary mb-sm">
                    {t('inventoryForm.issuesDesc')}
                  </p>
                  {issuesLoading ? (
                    <div className="flex items-center gap-sm text-secondary text-sm py-md">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('inventoryForm.loadingIssues')}
                    </div>
                  ) : issues.length === 0 ? (
                    <div className="border border-border rounded-lg p-md">
                      <p className="text-xs text-secondary italic">{t('inventoryForm.noIssues')}</p>
                    </div>
                  ) : (
                    <div className="space-y-sm border border-border rounded-lg p-md">
                      {issues.map((issue) => {
                        const sc: Record<string, { label: string; color: string; bg: string }> = {
                          reported: { label: t('inventoryForm.statusReported'), color: 'text-warning', bg: 'bg-warning/10' },
                          confirmed: { label: t('inventoryForm.statusConfirmed'), color: 'text-blue', bg: 'bg-blue/10' },
                          dismissed: { label: t('inventoryForm.statusDismissed'), color: 'text-secondary', bg: 'bg-secondary/10' },
                          repair_assigned: { label: t('inventoryForm.statusRepairAssigned'), color: 'text-danger', bg: 'bg-danger/10' },
                          resolved: { label: t('inventoryForm.statusResolved'), color: 'text-success', bg: 'bg-success/10' },
                          cannot_repair: { label: t('inventoryForm.statusResolved'), color: 'text-success', bg: 'bg-success/10' },
                          closed: { label: t('inventoryForm.statusClosed'), color: 'text-secondary', bg: 'bg-secondary/10' },
                        };
                        const conf = sc[issue.status] || sc.reported;
                        const isExpanded = expandedIssueId === issue.id;
                        return (
                          <div key={issue.id} className="border border-border rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setExpandedIssueId(isExpanded ? null : issue.id)}
                              className="w-full flex items-center justify-between p-sm bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-sm min-w-0">
                                <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${conf.bg} ${conf.color}`}>
                                  {conf.label}
                                </span>
                                <span className="text-sm font-medium text-primary truncate">
                                  {issue.title}
                                </span>
                              </div>
                              <span className="text-xs text-secondary flex-shrink-0 ml-sm">
                                {formatDate(issue.created_at)}
                              </span>
                            </button>
                            {isExpanded && (
                              <IssueDetail
                                issueId={issue.id}
                                merchantId={merchantId || ''}
                                onStatusChanged={loadIssues}
                                defaultExpanded={true}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Bookings (read-only) */}
          {activeTab === 'bookings' && showBookingsTab && (
            <div className="space-y-lg">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-md">
                <div>
                  <p className="text-sm text-secondary">
                    {t('inventoryForm.bookingsTabDescription')}
                  </p>
                  <p className="text-xs text-secondary mt-xs">
                    {t('inventoryForm.bookingsTabReadOnly')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/bookings?inventoryItem=${id}`)}
                  className="btn btn-ghost text-sm whitespace-nowrap"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('inventoryForm.viewAllInBookings')}
                </button>
              </div>

              {bookingsLoading && (
                <div className="flex items-center justify-center py-2xl">
                  <Loader2 className="w-6 h-6 animate-spin text-secondary" />
                </div>
              )}

              {bookingsError && !bookingsLoading && (
                <div className="card bg-danger/10 border border-danger/20">
                  <div className="flex items-center gap-md text-danger">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">{bookingsError}</p>
                  </div>
                </div>
              )}

              {!bookingsLoading && !bookingsError && bookings && bookings.length === 0 && (
                <div className="text-center py-2xl">
                  <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-secondary" />
                  </div>
                  <p className="text-secondary text-sm">{t('inventoryForm.noBookingsForItem')}</p>
                </div>
              )}

              {!bookingsLoading && !bookingsError && bookings && bookings.length > 0 && (() => {
                const now = Date.now();
                const cmpAsc = (a: any, b: any) => {
                  const sa = a.start_date ? new Date(a.start_date).getTime() : 0;
                  const sb = b.start_date ? new Date(b.start_date).getTime() : 0;
                  if (sa !== sb) return sa - sb;
                  const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
                  const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
                  return ca - cb;
                };
                const cmpReturnedDesc = (a: any, b: any) => {
                  const ra = a.returned_at
                    ? new Date(a.returned_at).getTime()
                    : a.end_date ? new Date(a.end_date).getTime() : 0;
                  const rb = b.returned_at
                    ? new Date(b.returned_at).getTime()
                    : b.end_date ? new Date(b.end_date).getTime() : 0;
                  return rb - ra;
                };
                const cmpStartDesc = (a: any, b: any) => {
                  const sa = a.start_date ? new Date(a.start_date).getTime() : 0;
                  const sb = b.start_date ? new Date(b.start_date).getTime() : 0;
                  return sb - sa;
                };

                const upcoming: any[] = [];
                const past: any[] = [];
                const cancelled: any[] = [];
                for (const b of bookings) {
                  if (b.booking_state === 'cancelled' || b.booking_state === 'no_show') {
                    cancelled.push(b);
                    continue;
                  }
                  const endTs = b.end_date ? new Date(b.end_date).getTime() : null;
                  if (b.booking_state === 'returned' || (endTs !== null && endTs < now)) {
                    past.push(b);
                  } else {
                    upcoming.push(b);
                  }
                }
                upcoming.sort(cmpAsc);
                past.sort(cmpReturnedDesc);
                cancelled.sort(cmpStartDesc);

                const visiblePast = showAllPast ? past : past.slice(0, PAST_BOOKINGS_LIMIT);
                const pastIsCapped = past.length > PAST_BOOKINGS_LIMIT;

                const renderRow = (booking: any) => (
                  <tr
                    key={booking.id}
                    className="hover:bg-secondary transition-colors cursor-pointer"
                    onClick={() => navigate(`/bookings/${booking.id}/edit`)}
                  >
                    <td className="py-md px-lg">
                      <span className="text-sm font-medium text-primary">
                        {booking.order_id || t('common.na')}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <div className="flex items-center gap-xs">
                        <User className="w-4 h-4 text-secondary" />
                        <span className="text-sm text-primary">
                          {[booking.customer_firstname, booking.customer_lastname].filter(Boolean).join(' ') || t('common.na')}
                        </span>
                      </div>
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm text-secondary">
                        {booking.start_date ? formatDate(booking.start_date) : '-'}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <span className="text-sm text-secondary">
                        {booking.end_date ? formatDate(booking.end_date) : '-'}
                      </span>
                    </td>
                    <td className="py-md px-lg">
                      <span className={`badge ${getBookingStateBadgeClass(booking.booking_state)}`}>
                        {booking.booking_state
                          ? String(t(`bookings.state_${booking.booking_state}`, { defaultValue: booking.booking_state }))
                          : t('common.na')}
                      </span>
                    </td>
                    <td className="py-md px-lg text-right">
                      <span className="text-sm font-medium text-primary">
                        {currencySymbol}{Number(booking.total_amount || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );

                const renderTable = (rows: any[]) => (
                  <div className="card p-0 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-primary">
                          <tr>
                            <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.bookingId')}</th>
                            <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.customer')}</th>
                            <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventoryItem.startDate')}</th>
                            <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('inventoryItem.endDate')}</th>
                            <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookings.status')}</th>
                            <th className="text-right py-md px-lg text-sm font-semibold text-primary">{t('bookings.amount')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {rows.map(renderRow)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );

                return (
                  <div className="space-y-xl">
                    {/* Active & upcoming */}
                    {upcoming.length > 0 && (
                      <div className="space-y-sm">
                        <h3 className="text-sm font-semibold text-primary">
                          {t('bookings.sectionUpcoming')} ({upcoming.length})
                        </h3>
                        {renderTable(upcoming)}
                      </div>
                    )}

                    {/* Past */}
                    {past.length > 0 && (
                      <div className="space-y-sm">
                        <h3 className="text-sm font-semibold text-primary">
                          {t('bookings.sectionPast')} ({past.length})
                        </h3>
                        {renderTable(visiblePast)}
                        {pastIsCapped && (
                          <button
                            type="button"
                            onClick={() => setShowAllPast(!showAllPast)}
                            className="text-sm text-blue hover:underline flex items-center gap-xs"
                          >
                            {showAllPast ? (
                              <>
                                <ChevronUp className="w-4 h-4" />
                                {t('bookings.showFewerPast')}
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-4 h-4" />
                                {t('bookings.showAllPast', { count: past.length })}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Cancelled & no-show (collapsed by default) */}
                    {cancelled.length > 0 && (
                      <div className="space-y-sm">
                        <button
                          type="button"
                          onClick={() => setShowCancelled(!showCancelled)}
                          className="text-sm font-semibold text-secondary hover:text-primary flex items-center gap-xs"
                        >
                          {showCancelled ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                          {showCancelled
                            ? t('bookings.hideCancelled')
                            : String(t('bookings.showCancelledCount', { count: cancelled.length }))}
                        </button>
                        {showCancelled && renderTable(cancelled)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tab 6: Usage Statistics */}
          {activeTab === 'statistics' && (
            <div className="space-y-lg">
              <div>
                <p className="text-sm text-secondary">
                  {t('products.usageStatisticsDescription')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
                {/* Total Rentals Card */}
                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center">
                      <RotateCcw className="w-5 h-5 text-blue" />
                    </div>
                    <span className="badge badge-info">{t('products.totalRentals')}</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">{formData.usage_times}</p>
                  <p className="text-xs text-secondary mt-xs">{t('inventory.totalRentalsDescription')}</p>
                </div>

                {/* Usage Hours Card */}
                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-success/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-success" />
                    </div>
                    <span className="badge badge-success">{t('products.usageHours')}</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">{formData.usage_hours.toFixed(2)}</p>
                  <p className="text-xs text-secondary mt-xs">{t('inventory.usageHoursDescription')}</p>
                </div>

                {/* Total Revenue Card */}
                <div className="card">
                  <div className="flex items-start justify-between mb-md">
                    <div className="w-10 h-10 rounded-md bg-warning/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-warning" />
                    </div>
                    <span className="badge badge-warning">{t('products.totalRevenue')}</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">
                    {currencySymbol}{formData.usage_revenue.toFixed(2)}
                  </p>
                  <p className="text-xs text-secondary mt-xs">{t('inventory.totalRevenueDescription')}</p>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue/5 border border-blue/20 rounded-lg p-md">
                <div className="flex items-start gap-md">
                  <Info className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-primary">{t('products.usageStatsNote')}</p>
                    <p className="text-xs text-secondary mt-xs">{t('products.usageStatsNoteDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
        </div>
      </form>

      {/* Action Buttons - Mobile Bottom */}
      <div className="sm:hidden flex flex-col gap-md">
        {isEditMode && (
          <button
            type="button"
            onClick={() => navigate(`/inventory/create?from=${id}`)}
            className="btn btn-ghost w-full"
            disabled={saving || deleting}
          >
            <Copy className="w-4 h-4" />
            {t('inventoryForm.duplicate')}
          </button>
        )}
        {isEditMode && (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="btn btn-ghost text-danger hover:bg-danger/10 w-full"
            disabled={saving || deleting}
          >
            <Trash2 className="w-4 h-4" />
            {t('inventoryForm.deleteItem')}
          </button>
        )}
        <div className="flex items-center gap-md">
          <button type="button" onClick={() => navigate('/inventory')} className="btn btn-ghost flex-1" disabled={saving || deleting}>
            <X className="w-4 h-4" />
            {t('common.cancel')}
          </button>
          <button type="button" onClick={handleSubmit} className="btn btn-primary flex-1" disabled={saving || deleting}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? t('inventoryForm.updateItem') : t('inventoryForm.createItem')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {/* Blocking Period Conflicts Modal */}
      {isEditMode && id && (
        <BlockingPeriodConflictsModal
          isOpen={showBlockingConflictsModal}
          onClose={() => setShowBlockingConflictsModal(false)}
          inventoryItemId={id}
          productTypeId={formData.product_type_id}
          blockingTimes={blockingTimes}
          onResolved={handleBlockingConflictsResolved}
          onProceedWithConflicts={handleProceedWithConflicts}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-md">
          <div className="bg-primary rounded-lg shadow-2xl max-w-md w-full p-xl">
            <div className="flex items-center gap-md mb-lg">
              <div className="w-12 h-12 rounded-full bg-danger/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-danger" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">{t('inventoryForm.deleteInventoryItem')}</h3>
                <p className="text-sm text-secondary">{t('inventoryForm.cannotBeUndone')}</p>
              </div>
            </div>

            {error ? (
              <>
                <div className="bg-danger/10 border border-danger/20 rounded-lg p-md mb-lg">
                  <p className="text-sm text-danger">{error}</p>
                </div>
                <p className="text-secondary mb-xl">
                  {t('inventoryForm.deactivateInstead')}
                </p>
                <div className="flex items-center justify-end gap-md">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setError(null);
                    }}
                    disabled={deleting}
                    className="btn btn-ghost"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeactivate}
                    disabled={deleting}
                    className="btn btn-secondary"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('inventoryForm.deactivating')}
                      </>
                    ) : (
                      <>
                        {t('inventoryForm.deactivateItem')}
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-secondary mb-xl">
                  {t('inventoryForm.deleteConfirmation')}
                </p>
                <div className="flex items-center justify-end gap-md">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className="btn btn-ghost"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="btn bg-danger text-white hover:bg-danger/90"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('inventoryForm.deleting')}
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        {t('inventoryForm.deleteItem')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {isEditMode && id && (
        <ReportIssueModal
          isOpen={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          onIssueCreated={() => {
            setShowReportIssueModal(false);
            loadIssues();
          }}
          initialData={{
            merchant_id: merchantId || '',
            inventory_item_id: id,
            product_type_id: formData.product_type_id || null,
            location_id: allProductTypes.find(pt => pt.id === formData.product_type_id)?.location_id || null,
            item_label: formData.item_number || t('inventoryForm.inventoryItem'),
          }}
        />
      )}
    </div>
  );
}
